/**
 * The New Patient form's validation errors, keyed by catalog field key
 * (`first_name`, `cell_phone`, ...), and the list the "Please fix these
 * fields" modal shows (R11, KTD8).
 *
 * Pure so tests can import it. Errors from three places land on the same keys:
 *  - the browser's required check (requiredErrors),
 *  - the save's REQUIRED_FIELDS 422 (`details.fields` is already keyed by
 *    catalog key),
 *  - the save's VALIDATION_ERROR / DUPLICATE_CREDENTIALS 422s, keyed by
 *    payload key (`cellPhone`), mapped through PAYLOAD_KEY_FIELDS.
 */
import {
  displayLabel,
  inRenderOrder,
  missingRequired,
  type FormMode,
  type FormState,
  type RequiredContext,
  type ResolvedField,
} from './newPatientFormRules';

/** The scroll/focus anchor id on a field's wrapper. */
export function fieldAnchorId(key: string): string {
  return `field-${key}`;
}

/**
 * A required field left empty, named by its displayed label (R11). Today's
 * form words it this way (KTD21); the save uses the same pattern
 * (IntakeFieldRules::missingRequired).
 */
export function requiredMessage(label: string): string {
  return `${label} is required`;
}

/**
 * Today's exact messages for the fields today's form requires, used while
 * the field has no custom label (KTD21). Taken from the hard-coded
 * NewLeadPage validate() and not reworded; the save keeps the same list
 * (IntakeFieldRules::TODAY_MESSAGES).
 */
const TODAY_REQUIRED_MESSAGES: Readonly<Record<string, string>> = {
  first_name: 'First name is required',
  birth_date: 'Date of birth is required',
  cell_phone: 'Cell phone is required',
  email: 'Email is required',
  plan_type: 'Plan type is required',
  facility: 'Facility is required',
  referral_select_1_anchor: 'Lead source category is required',
};

/** How the form labels the first of the two name inputs. */
const FIRST_NAME_LABEL = 'First Name';

/** How the form labels the second of the two name inputs. */
export const LAST_NAME_LABEL = 'Last Name';

/** The Last Name input's own error key; the name setting covers both inputs. */
export const LAST_NAME_ERROR_KEY = 'last_name';

const LAST_NAME_REQUIRED_MESSAGE = 'Last name is required';

/**
 * The Last Name input in the error list: listed straight after First Name,
 * as today's form lists the two names as separate entries (KTD21).
 */
export const LAST_NAME_ERROR_FIELD: ExtraErrorField = {
  key: LAST_NAME_ERROR_KEY,
  label: LAST_NAME_LABEL,
  anchorId: fieldAnchorId(LAST_NAME_ERROR_KEY),
  afterKey: 'first_name',
};

function hasCustomLabel(field: ResolvedField | undefined): boolean {
  return Boolean(field?.customLabel?.trim());
}

/** Required fields left empty in this mode, keyed by field key. */
export function requiredErrors(
  fields: readonly ResolvedField[],
  mode: FormMode,
  state: FormState,
  captions: Record<string, string> = {},
  context?: RequiredContext,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const missing of missingRequired(fields, mode, state, captions, context)) {
    const field = fields.find((f) => f.key === missing.key);
    if (hasCustomLabel(field)) {
      out[missing.key] = requiredMessage(missing.label);
      continue;
    }
    // The name setting is one field over two inputs: with no custom label each
    // empty input gets today's own entry, First Name then Last Name.
    if (missing.key === 'first_name') {
      if (state.firstName.trim() === '') out.first_name = TODAY_REQUIRED_MESSAGES.first_name;
      if (state.lastName.trim() === '') out[LAST_NAME_ERROR_KEY] = LAST_NAME_REQUIRED_MESSAGE;
      continue;
    }
    out[missing.key] = TODAY_REQUIRED_MESSAGES[missing.key] ?? requiredMessage(missing.label);
  }
  return out;
}

/**
 * Intake payload key → the error key it belongs to. Mirrors
 * INTAKE_PAYLOAD_KEYS; the referring-patient keys land on the Referring
 * Patient input, which is not a catalog field.
 */
export const PAYLOAD_KEY_FIELDS: Readonly<Record<string, string>> = {
  firstName: 'first_name',
  lastName: LAST_NAME_ERROR_KEY,
  middleName: 'middle_name',
  maidenName: 'maiden_name',
  gender: 'gender',
  birthDate: 'birth_date',
  cellPhone: 'cell_phone',
  cellCountryCode: 'cell_phone',
  email: 'email',
  address: 'address',
  city: 'city',
  state: 'state',
  zip: 'zip',
  countryId: 'country',
  planTypeId: 'plan_type',
  facilityId: 'facility',
  leadSourceId: 'referral_select_1_anchor',
  referralSourceId: 'referral_name',
  referringPatientId: 'referralPatient',
  referralFirstname: 'referralPatient',
  referralLastname: 'referralPatient',
  doctorId: 'doctor',
  coordinatorId: 'coordinator',
  nurseId: 'nurse',
  revenueOpportunity: 'ball_park_given',
  commitmentLevel: 'ball_park',
  timeframe: 'timeframe',
  notes: 'notes',
};

/**
 * A save's per-field messages (`details.fields` or `details.errors`) keyed
 * onto error keys. Catalog keys pass through, payload keys are mapped, and
 * anything else is kept as sent so it still reaches the list. The first
 * message for a key wins.
 */
export function toFieldErrors(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const message = Array.isArray(value) ? value[0] : value;
    if (typeof message !== 'string' || message.trim() === '') continue;
    const target = PAYLOAD_KEY_FIELDS[key] ?? key;
    if (!(target in out)) out[target] = message;
  }
  return out;
}

/** An error key that is not a catalog field but has its own input. */
export interface ExtraErrorField {
  key: string;
  label: string;
  anchorId: string;
  /** Listed straight after this field key. */
  afterKey: string;
}

export interface ErrorListEntry {
  key: string;
  /** Absent when the error has no input to jump to. */
  anchorId?: string;
  label: string;
  message: string;
}

/** Label for an error that belongs to no input on the form. */
export const OTHER_ERROR_LABEL = 'Other details';

/**
 * The modal's list: every field with an error, in render order (card order,
 * then order within the card), labelled as the form labels it; extra inputs
 * after the field they follow; then anything left over, so no message is
 * ever dropped.
 */
export function buildErrorList(args: {
  fields: readonly ResolvedField[];
  errors: Record<string, string>;
  captions?: Record<string, string>;
  extras?: readonly ExtraErrorField[];
}): ErrorListEntry[] {
  const { errors, captions = {}, extras = [] } = args;
  const list: ErrorListEntry[] = [];
  const listed = new Set<string>();

  const pushExtras = (afterKey: string | null) => {
    for (const extra of extras) {
      if (listed.has(extra.key) || !errors[extra.key]) continue;
      if (afterKey !== null && extra.afterKey !== afterKey) continue;
      list.push({ key: extra.key, anchorId: extra.anchorId, label: extra.label, message: errors[extra.key] });
      listed.add(extra.key);
    }
  };

  for (const field of inRenderOrder(args.fields)) {
    if (errors[field.key]) {
      list.push({
        key: field.key,
        anchorId: fieldAnchorId(field.key),
        // With no custom label the name setting's entry is the First Name
        // input's, labelled as that input is (Last Name has its own entry).
        label: field.key === 'first_name' && !hasCustomLabel(field) ? FIRST_NAME_LABEL : displayLabel(field, captions),
        message: errors[field.key],
      });
      listed.add(field.key);
    }
    pushExtras(field.key);
  }
  pushExtras(null);

  for (const key of Object.keys(errors)) {
    if (listed.has(key) || !errors[key]) continue;
    list.push({ key, label: OTHER_ERROR_LABEL, message: errors[key] });
  }
  return list;
}

/** The anchor of the topmost error that has an input. */
export function firstErrorAnchor(list: readonly ErrorListEntry[]): string | undefined {
  return list.find((entry) => entry.anchorId)?.anchorId;
}
